/**
 * 装配组件拖拽自由度能力解析服务。
 *
 * 文件职责：
 * 1. 基于已提交 Mate 求解链路判断组件当前是否还能平移或旋转；
 * 2. 为默认左键拖拽和“移动零部件”命令提供 SOLIDWORKS 风格的 Move -> Rotate 自适应决策；
 * 3. 所有探测写入都在服务内部回滚，避免选择层或拖拽策略散落临时状态修补逻辑。
 */

import * as THREE from 'three';
import type { Viewer } from '../../viewer/Viewer';
import { AssemblyComponentFixationService } from '../component-fixation';
import {
    applyComponentInstanceSnapshotToObject,
    createComponentInstanceSnapshotFromObject,
    type ComponentInstanceSnapshot,
} from '../component-instance';
import { MateSolveStatus } from '../definition';
import { AssemblyTransformToolMode } from './MoveComponentTypes';
import { resolveMoveComponentTransformTarget } from './MoveComponentTransformTargetResolver';
import { resolveAssemblyComponentLinkedTransformObjects } from './AssemblyComponentTransformLinkResolver';
import { createAssemblyComponentDrivenConstraintsForToolMode } from './AssemblyComponentTransformIntent';

/** 平移能力探测距离，单位沿用当前渲染世界坐标。 */
const TRANSLATION_PROBE_DISTANCE = 1e-3;

/** 旋转能力探测角度。 */
const ROTATION_PROBE_ANGLE = THREE.MathUtils.degToRad(1);

/** 平移探测被求解器接收后应保留的最小位置变化量。 */
const TRANSLATION_PROBE_ACCEPT_EPSILON = TRANSLATION_PROBE_DISTANCE * 1e-3;

/** 旋转探测被求解器接收后应保留的最小角度变化量。 */
const ROTATION_PROBE_ACCEPT_EPSILON = ROTATION_PROBE_ANGLE * 1e-3;

/** 平移和旋转探测共用的世界坐标轴集合。 */
const WORLD_PROBE_AXES: readonly THREE.Vector3[] = [
    new THREE.Vector3(1, 0, 0),
    new THREE.Vector3(0, 1, 0),
    new THREE.Vector3(0, 0, 1),
];

/**
 * 装配组件拖拽能力解析输入。
 */
export interface AssemblyComponentDragCapabilityResolveInput {
    /** 当前命中的组件或拓扑代理最终解析出的 transform 根。 */
    readonly targetObject: THREE.Object3D | null | undefined;
    /** 调用方请求的工具模式。 */
    readonly requestedToolMode: AssemblyTransformToolMode;
    /** 请求 Move 时是否允许在平移不可用但旋转可用时降级为 Rotate。 */
    readonly allowRotateFallbackWhenMoveBlocked?: boolean;
}

/**
 * 装配组件拖拽能力解析结果。
 */
export interface AssemblyComponentDragCapabilityResolveResult {
    /** 当前组件是否具备任意可解平移自由度。 */
    readonly canMove: boolean;
    /** 当前组件是否具备任意可解旋转自由度。 */
    readonly canRotate: boolean;
    /** 本次拖拽最终应启动的工具模式；为 `null` 时不应启动组件拖拽。 */
    readonly resolvedToolMode: AssemblyTransformToolMode | null;
}

/**
 * 组件快照恢复目标。
 */
interface ComponentSnapshotRestoreTarget {
    /** 需要恢复的场景对象。 */
    readonly object: THREE.Object3D;
    /** 探测前的组件快照。 */
    readonly snapshot: ComponentInstanceSnapshot;
}

/**
 * 装配组件拖拽自由度能力解析服务。
 *
 * 生命周期：由选择处理器持有，不创建 WebGL 资源；每次拖拽启动阈值后同步解析一次。
 */
export class AssemblyComponentDragCapabilityResolver {
    /** Viewer 实例。 */
    private readonly viewer: Viewer;

    /**
     * 创建能力解析服务。
     *
     * @param viewer Viewer 实例。
     */
    public constructor(viewer: Viewer) {
        this.viewer = viewer;
    }

    /**
     * 判断探测后是否保留了当前工具真正请求的变换分量。
     *
     * 设计说明：
     * 闭环机构中，Move 探测可能释放旋转分量用于求解；如果只判断完整 transform 是否变化，会把“仅发生旋转”
     * 误判为“可平移”。这里按工具模式分别检查平移或旋转分量，保证左键优先平移的判定来自真实剩余平移自由度。
     *
     * @param toolMode 当前探测工具模式。
     * @param beforeSnapshot 探测前组件快照。
     * @param afterSnapshot 探测后组件快照。
     * @returns 请求分量确实被求解器保留时返回 `true`。
     */
    private hasRequestedProbeComponentChanged(
        toolMode: AssemblyTransformToolMode,
        beforeSnapshot: ComponentInstanceSnapshot,
        afterSnapshot: ComponentInstanceSnapshot,
    ): boolean {
        if (toolMode === AssemblyTransformToolMode.Move) {
            const deltaX = afterSnapshot.transform.position[0] - beforeSnapshot.transform.position[0];
            const deltaY = afterSnapshot.transform.position[1] - beforeSnapshot.transform.position[1];
            const deltaZ = afterSnapshot.transform.position[2] - beforeSnapshot.transform.position[2];
            if (this.calculateSnapshotRotationAngle(beforeSnapshot, afterSnapshot) > ROTATION_PROBE_ACCEPT_EPSILON) {
                return false;
            }
            return deltaX * deltaX + deltaY * deltaY + deltaZ * deltaZ
                > TRANSLATION_PROBE_ACCEPT_EPSILON * TRANSLATION_PROBE_ACCEPT_EPSILON;
        }

        return this.calculateSnapshotRotationAngle(beforeSnapshot, afterSnapshot) > ROTATION_PROBE_ACCEPT_EPSILON;
    }

    /**
     * 计算两个组件快照之间的旋转角度差。
     *
     * @param beforeSnapshot 探测前组件快照。
     * @param afterSnapshot 探测后组件快照。
     * @returns 两个快照之间的最小四元数夹角。
     */
    private calculateSnapshotRotationAngle(
        beforeSnapshot: ComponentInstanceSnapshot,
        afterSnapshot: ComponentInstanceSnapshot,
    ): number {
        const beforeQuaternion = beforeSnapshot.transform.quaternion;
        const afterQuaternion = afterSnapshot.transform.quaternion;
        const quaternionDot = Math.abs(
            beforeQuaternion[0] * afterQuaternion[0]
            + beforeQuaternion[1] * afterQuaternion[1]
            + beforeQuaternion[2] * afterQuaternion[2]
            + beforeQuaternion[3] * afterQuaternion[3],
        );
        const clampedDot = Math.min(1, Math.max(-1, quaternionDot));
        return 2 * Math.acos(clampedDot);
    }

    /**
     * 解析当前目标可启动的拖拽模式。
     *
     * @param input 解析输入。
     * @returns 拖拽能力解析结果。
     */
    public resolveDragCapability(
        input: AssemblyComponentDragCapabilityResolveInput,
    ): AssemblyComponentDragCapabilityResolveResult {
        const transformTarget = resolveMoveComponentTransformTarget(input.targetObject as never, this.viewer as never) as THREE.Object3D | null;
        if (!transformTarget) {
            return this.createBlockedResult();
        }

        const linkedTransformObjects = resolveAssemblyComponentLinkedTransformObjects(
            this.viewer as never,
            transformTarget,
        );
        if (
            linkedTransformObjects.length === 0
            || linkedTransformObjects.some((object) => !AssemblyComponentFixationService.canTransformSceneObject(object as never))
        ) {
            return this.createBlockedResult();
        }

        const canMove = input.requestedToolMode === AssemblyTransformToolMode.Move
            ? this.canProbeTransform(
                transformTarget,
                linkedTransformObjects,
                AssemblyTransformToolMode.Move,
            )
            : false;
        const shouldProbeRotate = input.requestedToolMode === AssemblyTransformToolMode.Rotate
            || (input.allowRotateFallbackWhenMoveBlocked === true && !canMove);
        const canRotate = shouldProbeRotate
            ? this.canProbeTransform(
                transformTarget,
                linkedTransformObjects,
                AssemblyTransformToolMode.Rotate,
            )
            : false;

        return {
            canMove,
            canRotate,
            resolvedToolMode: this.resolveToolMode({
                requestedToolMode: input.requestedToolMode,
                allowRotateFallbackWhenMoveBlocked: input.allowRotateFallbackWhenMoveBlocked === true,
                canMove,
                canRotate,
            }),
        };
    }

    /**
     * 创建完全不可拖拽的解析结果。
     *
     * @returns 阻断结果。
     */
    private createBlockedResult(): AssemblyComponentDragCapabilityResolveResult {
        return {
            canMove: false,
            canRotate: false,
            resolvedToolMode: null,
        };
    }

    /**
     * 根据请求模式和探测结果决策最终工具模式。
     *
     * @param input 决策输入。
     * @returns 最终工具模式。
     */
    private resolveToolMode(input: {
        readonly requestedToolMode: AssemblyTransformToolMode;
        readonly allowRotateFallbackWhenMoveBlocked: boolean;
        readonly canMove: boolean;
        readonly canRotate: boolean;
    }): AssemblyTransformToolMode | null {
        if (input.requestedToolMode === AssemblyTransformToolMode.Rotate) {
            return input.canRotate ? AssemblyTransformToolMode.Rotate : null;
        }

        if (input.canMove) {
            return AssemblyTransformToolMode.Move;
        }

        if (input.allowRotateFallbackWhenMoveBlocked && input.canRotate) {
            return AssemblyTransformToolMode.Rotate;
        }

        return null;
    }

    /**
     * 探测指定变换模式是否存在任意可解自由度。
     *
     * @param targetObject 当前组件根。
     * @param linkedTransformObjects 需要联动写入 transform 的对象集合。
     * @param toolMode 探测模式。
     * @returns 任一探测方向可被 Mate 求解器接受时返回 `true`。
     */
    private canProbeTransform(
        targetObject: THREE.Object3D,
        linkedTransformObjects: readonly THREE.Object3D[],
        toolMode: AssemblyTransformToolMode,
    ): boolean {
        const anchorSnapshot = createComponentInstanceSnapshotFromObject({
            object: targetObject as never,
        });

        if (toolMode === AssemblyTransformToolMode.Rotate) {
            return this.canProbeRotate(
                targetObject,
                linkedTransformObjects,
                anchorSnapshot.componentInstanceId,
            );
        }

        return WORLD_PROBE_AXES.some((axisWorld) => this.tryProbeTransform({
            targetObject,
            linkedTransformObjects,
            anchorComponentInstanceId: anchorSnapshot.componentInstanceId,
            toolMode,
            applyProbe: () => {
                this.applyWorldTranslationProbe(linkedTransformObjects, axisWorld);
            },
        }));
    }

    /**
     * 探测当前组件是否存在任意可解旋转自由度。
     *
     * 设计说明：
     * 能力探测必须与真实拖拽/旋转命令使用同一套旋转语义，否则会出现“求解器能转、但拖拽入口判定不可转”的分裂。
     * 1. 组件（或其所在浮动刚性组）若解析出对外部零件的转动副轴线，则整组绕该轴线刚体旋转一次进行探测；
     *    这与 `AssemblyPartDragStrategy` / `RotateService` 的受约束旋转完全一致，避免把可解的刚性块误判为不可旋转。
     * 2. 没有约束轴时按自由旋转探测：整组绕“共同包围盒中心”刚体旋转，绝不能让联动组各自绕自身中心旋转，
     *    否则会破坏刚性组内部约束、把本可自由自转的刚性块误判为不可旋转。
     *
     * @param targetObject 当前组件根。
     * @param linkedTransformObjects 需要联动写入 transform 的对象集合。
     * @param anchorComponentInstanceId 交互锚点组件实例 ID。
     * @returns 任一探测可被 Mate 求解器接受时返回 `true`。
     */
    private canProbeRotate(
        targetObject: THREE.Object3D,
        linkedTransformObjects: readonly THREE.Object3D[],
        anchorComponentInstanceId: string,
    ): boolean {
        const rotationAxis = this.viewer.assemblyMateController?.resolveComponentRotationAxisForTransform?.({
            componentInstanceId: anchorComponentInstanceId,
        }) ?? null;
        if (rotationAxis) {
            const axisOrigin = new THREE.Vector3().fromArray(rotationAxis.origin as readonly number[]);
            const axisDirection = new THREE.Vector3().fromArray(rotationAxis.direction as readonly number[]);
            return this.tryProbeTransform({
                targetObject,
                linkedTransformObjects,
                anchorComponentInstanceId,
                toolMode: AssemblyTransformToolMode.Rotate,
                applyProbe: () => this.applyRigidRotationProbe(linkedTransformObjects, axisDirection, axisOrigin),
            });
        }

        const commonPivotWorld = this.resolveLinkedRotationPivotWorld(linkedTransformObjects);
        return WORLD_PROBE_AXES.some((axisWorld) => this.tryProbeTransform({
            targetObject,
            linkedTransformObjects,
            anchorComponentInstanceId,
            toolMode: AssemblyTransformToolMode.Rotate,
            applyProbe: () => this.applyRigidRotationProbe(linkedTransformObjects, axisWorld, commonPivotWorld),
        }));
    }

    /**
     * 执行一次临时变换探测并保证场景回滚。
     *
     * @param input 探测输入。
     * @returns 探测变换可解时返回 `true`。
     */
    private tryProbeTransform(input: {
        readonly targetObject: THREE.Object3D;
        readonly linkedTransformObjects: readonly THREE.Object3D[];
        readonly anchorComponentInstanceId: string;
        readonly toolMode: AssemblyTransformToolMode;
        readonly applyProbe: () => void;
    }): boolean {
        const linkedSnapshots = this.captureComponentSnapshots(input.linkedTransformObjects);
        const targetBeforeSnapshot = createComponentInstanceSnapshotFromObject({
            object: input.targetObject as never,
        });
        let solveResult: ReturnType<NonNullable<Viewer['assemblyMateController']>['solveCommittedMatesAfterComponentTransform']> | null = null;

        try {
            input.applyProbe();
            solveResult = this.viewer.assemblyMateController?.solveCommittedMatesAfterComponentTransform({
                anchorComponentInstanceId: input.anchorComponentInstanceId,
                drivenComponentConstraints: createAssemblyComponentDrivenConstraintsForToolMode(
                    input.anchorComponentInstanceId,
                    input.toolMode,
                ),
                updateDocumentStatus: false,
                // 能力探测按各轴/各方向试错，失败是预期行为，不进入求解失败采集。
                unsolvedCaptureContext: null,
            }) ?? null;

            if (solveResult && solveResult.status !== MateSolveStatus.Solved) {
                return false;
            }

            const targetAfterSnapshot = createComponentInstanceSnapshotFromObject({
                object: input.targetObject as never,
                componentInstanceId: targetBeforeSnapshot.componentInstanceId,
            });
            return this.hasRequestedProbeComponentChanged(
                input.toolMode,
                targetBeforeSnapshot,
                targetAfterSnapshot,
            );
        } finally {
            this.restoreSolveResultBeforeSnapshots(solveResult?.beforeSnapshots ?? []);
            this.restoreComponentSnapshots(linkedSnapshots);
        }
    }

    /**
     * 捕获组件对象当前快照。
     *
     * @param objects 场景对象集合。
     * @returns 可恢复快照集合。
     */
    private captureComponentSnapshots(
        objects: readonly THREE.Object3D[],
    ): ComponentSnapshotRestoreTarget[] {
        return objects.map((object) => ({
            object,
            snapshot: createComponentInstanceSnapshotFromObject({
                object: object as never,
            }),
        }));
    }

    /**
     * 恢复直接探测对象快照。
     *
     * @param targets 快照恢复目标集合。
     */
    private restoreComponentSnapshots(targets: readonly ComponentSnapshotRestoreTarget[]): void {
        targets.forEach((target) => {
            applyComponentInstanceSnapshotToObject(target.object as never, target.snapshot);
        });
    }

    /**
     * 恢复求解器在可解探测中联动修改过的组件。
     *
     * @param snapshots 求解器返回的变换前快照集合。
     */
    private restoreSolveResultBeforeSnapshots(snapshots: readonly ComponentInstanceSnapshot[]): void {
        snapshots.forEach((snapshot) => {
            const object = this.viewer.assemblyMateController?.resolveSceneObjectForComponentSnapshot(snapshot) as THREE.Object3D | null | undefined;
            if (!object) {
                return;
            }
            applyComponentInstanceSnapshotToObject(object as never, snapshot);
        });
    }

    /**
     * 对联动对象集合应用小步长世界平移探测。
     *
     * @param linkedTransformObjects 联动对象集合。
     * @param axisWorld 世界坐标轴。
     */
    private applyWorldTranslationProbe(
        linkedTransformObjects: readonly THREE.Object3D[],
        axisWorld: THREE.Vector3,
    ): void {
        const deltaWorld = axisWorld.clone().multiplyScalar(TRANSLATION_PROBE_DISTANCE);
        linkedTransformObjects.forEach((object) => {
            const nextWorldPosition = object.getWorldPosition(new THREE.Vector3()).add(deltaWorld);
            if (object.parent) {
                object.position.copy(object.parent.worldToLocal(nextWorldPosition));
            } else {
                object.position.copy(nextWorldPosition);
            }
            object.updateMatrixWorld(true);
        });
    }

    /**
     * 对联动对象集合应用绕“共同世界枢轴”的小角度刚体旋转探测。
     *
     * 设计说明：
     * 联动组必须整组绕同一个世界枢轴旋转，保持组内相对位姿不变；绝不能让各成员各自绕自身中心旋转，
     * 否则刚性组内部约束会被探测本身破坏，导致本可旋转的刚性块被误判为不可旋转。约束轴探测传入轴上原点作为
     * 枢轴（等价于绕转动副轴线转动），自由旋转探测传入整组包围盒中心作为枢轴。
     *
     * @param linkedTransformObjects 联动对象集合。
     * @param axisWorld 世界坐标旋转轴。
     * @param pivotWorld 共同世界旋转枢轴。
     */
    private applyRigidRotationProbe(
        linkedTransformObjects: readonly THREE.Object3D[],
        axisWorld: THREE.Vector3,
        pivotWorld: THREE.Vector3,
    ): void {
        const normalizedAxis = axisWorld.clone().normalize();
        if (normalizedAxis.lengthSq() <= 0) {
            return;
        }
        const rotationMatrix = new THREE.Matrix4().makeRotationAxis(normalizedAxis, ROTATION_PROBE_ANGLE);
        const worldDeltaMatrix = new THREE.Matrix4()
            .makeTranslation(pivotWorld.x, pivotWorld.y, pivotWorld.z)
            .multiply(rotationMatrix)
            .multiply(new THREE.Matrix4().makeTranslation(-pivotWorld.x, -pivotWorld.y, -pivotWorld.z));

        linkedTransformObjects.forEach((object) => {
            object.updateMatrixWorld(true);
            const nextWorldMatrix = worldDeltaMatrix.clone().multiply(object.matrixWorld);
            const nextLocalMatrix = new THREE.Matrix4();

            if (object.parent) {
                object.parent.updateMatrixWorld(true);
                nextLocalMatrix.copy(object.parent.matrixWorld).invert().multiply(nextWorldMatrix);
            } else {
                nextLocalMatrix.copy(nextWorldMatrix);
            }

            nextLocalMatrix.decompose(object.position, object.quaternion, object.scale);
            object.updateMatrixWorld(true);
        });
    }

    /**
     * 解析联动对象组的共同世界包围盒中心作为自由旋转探测枢轴。
     *
     * @param linkedTransformObjects 联动对象集合。
     * @returns 整组世界空间旋转枢轴；包围盒为空时退回首个对象世界位置。
     */
    private resolveLinkedRotationPivotWorld(
        linkedTransformObjects: readonly THREE.Object3D[],
    ): THREE.Vector3 {
        const bounds = new THREE.Box3();
        linkedTransformObjects.forEach((object) => {
            const sourceMeshes = this.viewer.listImportedModelInstancedSourceMeshes?.(object) ?? [];
            if (sourceMeshes.length === 0) {
                object.updateMatrixWorld(true);
                bounds.expandByObject(object);
                return;
            }
            sourceMeshes.forEach((sourceMesh) => {
                this.viewer.prepareImportedModelSourceMeshForSpatialRead?.(sourceMesh);
                bounds.expandByObject(sourceMesh);
            });
        });
        if (!bounds.isEmpty()) {
            return bounds.getCenter(new THREE.Vector3());
        }

        return linkedTransformObjects[0]?.getWorldPosition(new THREE.Vector3()) ?? new THREE.Vector3();
    }
}
